import PropTypes from "prop-types";
import classNames from "classnames";

export const Image = ({
    alt = "",
    aspectRatio = null,
    credit = "",
    description = "",
    mass = "full",
    showcaption = false,
    sizes = [],
    srcSet = [],
    type = "image",
    url = "",
    isAboveFold = false,
    className,
    loading,
}) => {
    /**
     * Return the JSX for the image caption, if data was provided.
     * @return {component} JSX markup for the image.
     */
    const getCaption = () => {
        if (showcaption === false) {
            return null;
        }
        const imageDescription = description.length ? description : null;
        const imageCredit = credit.length ? `(${credit})` : null;

        if (imageDescription !== null || imageCredit !== null) {
            return (
                <figcaption className={"image-caption"}>
                    {imageDescription} {imageCredit}
                </figcaption>
            );
        }
        return null;
    };

    /**
     * Return the <img> tag with all the properties.
     * @return {component} JSX markup for the image.
     */
    const getImageToRender = () => {
        let imageURL = "";
        let imageSrcSet = null;
        let imageSizes = null;
        let style = {};

        if (url !== undefined) {
            imageURL = url;
        } else {
            // In certain cases, like some search results, the image URL might not be provided
            return null;
        }

        if (srcSet?.length) {
            imageSrcSet = srcSet.join(",");
        }

        if (sizes?.length) {
            imageSizes = sizes.join(",");
        }

        if (typeof aspectRatio === "number") {
            style = {
                aspectRatio,
            };
        }

        const imgLoading = loading === null ? null : "lazy";

        const fetchPriorityAttribute = isAboveFold
            ? { fetchpriority: "high" }
            : {};

        return (
            <div className="placeholder">
                <img
                    loading={imgLoading}
                    alt={alt}
                    srcSet={imageSrcSet}
                    sizes={imageSizes}
                    src={imageURL}
                    style={style}
                    data-testid="image-img"
                    data-cy={`${type}-img`}
                    {...fetchPriorityAttribute}
                />
            </div>
        );
    };

    const classes = classNames(
        "imageMedia",
        type,
        mass || mass !== "" ? mass : "full",
        className
    );

    return (
        <figure className={classes} data-testid="image-figure">
            {getImageToRender()}
            {getCaption()}
        </figure>
    );
};

Image.propTypes = {
    alt: PropTypes.string,
    credit: PropTypes.string,
    description: PropTypes.string,
    mass: PropTypes.string,
    showcaption: PropTypes.bool,
    sizes: PropTypes.arrayOf(PropTypes.string),
    srcSet: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.string,
    url: PropTypes.string,
    loading: PropTypes.string,
    aspectRatio: PropTypes.number,
    isAboveFold: PropTypes.bool,
    className: PropTypes.string,
};
